import * as THREE from 'three';
import { FileLoader } from 'three';
import { Octree } from './Octree';
import { OctreeNode } from './OctreeNode';

// Loads json hierarchy file

interface OctreeNodeJson {
  key: string;
  numPoints: number;
  // x0,y0,z0,x1,y1,z1
  bbox: number[];
}

interface OctreeFieldJson {
  name: string;
  type: string;
  offset: number;
  size: number;
}

interface OctreeHierarchyJson {
  nodes: OctreeNodeJson[];
  pathPrefix?: string;
  recordSize: number;
  fields: OctreeFieldJson[];
}

export class OctreeHierarchyLoader extends THREE.Loader {
  load(
    url: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onLoad: (argument0: any) => void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onProgress: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (argument0: any) => void
  ) {
    // THREE.Cache.enabled = true;

    const loader = new FileLoader(this.manager);

    loader.setResponseType('text');

    loader.setRequestHeader(this.requestHeader);
    loader.setWithCredentials(this.withCredentials);
    loader.load(
      url,
      (dataTxt) => {
        try {
          onLoad(this.parse(dataTxt as string, url));
        } catch (error) {
          if (onError) {
            onError(error);
          } else {
            console.error(error);
          }

          this.manager.itemError(url);
        }
      },
      onProgress,
      onError
    );
  }

  parse(dataTxt: string, url: string): Octree {
    const data = JSON.parse(dataTxt as string) as OctreeHierarchyJson;

    const basePath = `${url.slice(0, Math.max(0, url.lastIndexOf('/')))}/`;

    const pathPrefix = basePath + (data.pathPrefix ?? '');

    const nodes = new Map<string, OctreeNode>();

    const dataFormat = data.fields
      ? { fields: data.fields, recordSize: data.recordSize }
      : undefined;

    if (!dataFormat) {
      throw new Error(`Unknown data format - missing 'fields' in ${url}`);
    }

    const gz_extension = url.endsWith('.gz') ? '.gz' : '';

    let totalNumPoints = 0;

    data.nodes.forEach((node: OctreeNodeJson) => {
      const nodePath = `${pathPrefix}${node.key}.bin${gz_extension}`;

      const bboxMin = new THREE.Vector3(node.bbox[0], node.bbox[1], node.bbox[2]);
      const bboxMax = new THREE.Vector3(node.bbox[3], node.bbox[4], node.bbox[5]);
      const bbox = new THREE.Box3(bboxMin, bboxMax);

      const octreeNode = new OctreeNode(
        node.key,
        nodePath,
        node.numPoints,
        bbox,
        dataFormat,
        this.requestHeader
      );

      totalNumPoints += node.numPoints;

      nodes.set(node.key, octreeNode);
    });

    let root = null;

    nodes.forEach((node: OctreeNode, key: string) => {
      if (key === 'r') {
        root = node;
      } else {
        const parentKey = key.slice(0, Math.max(0, key.length - 1));
        const parent = nodes.get(parentKey);
        if (parent != null) {
          parent.children.push(node);
        } else {
          throw new Error(`Invalid hierarchy file. Node ${key} parent ${parentKey} not found`);
        }
      }
    });

    if (root == null) {
      throw new Error('Invalid hierarchy file. Root node is not found');
    }

    return new Octree(url, root, totalNumPoints);
  }
}
